import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import {
    MdCheckCircle,
    MdCancel,
    MdVisibility,
    MdPending,
    MdInventory,
    MdMerge,
    MdNewReleases,
} from 'react-icons/md';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import StatCard from '../../../../shared/layout/cards/StatCard';
import StatsGrid from '../../../../shared/layout/containers/StatsGrid';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import { fetchManufacturerPendingProducts, fetchManufacturerPendingProductsStats } from '../../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import { SimpleSelect } from '../../../../shared/layout/forms/AppSelect';

const ManufacturerProductApprovals = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [perPage, setPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [searchTerm, setSearchTerm] = useState('');

    const loadStats = async () => {
        try {
            const data = await fetchManufacturerPendingProductsStats();
            setStats(data);
        } catch (err) {
            // non-blocking
        }
    };

    const loadProducts = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchManufacturerPendingProducts({
                per_page: perPage,
                page: currentPage,
                status: statusFilter || undefined,
                search: searchTerm || undefined,
            });
            const rowsData = data?.data || data || [];
            const meta = data?.meta;
            setRows(rowsData);
            setTotalRows(meta?.total ?? rowsData.length);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [perPage, currentPage, statusFilter, searchTerm]);

    useEffect(() => {
        loadStats();
        loadProducts();
    }, [loadProducts]);

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { label: t('ProductApprovals.status.pending'), color: '#F59E0B', bg: '#FEF3C7' },
            approved: { label: t('ProductApprovals.status.approved'), color: '#22C55E', bg: '#DCFCE7' },
            rejected: { label: t('ProductApprovals.status.rejected'), color: '#EF4444', bg: '#FEE2E2' },
            merged: { label: t('ProductApprovals.status.merged'), color: '#3B82F6', bg: '#DBEAFE' },
        };
        const config = statusConfig[status] || { label: status, color: '#6B7280', bg: '#F3F4F6' };
        return <StatusBadge $color={config.color} $bg={config.bg}>{config.label}</StatusBadge>;
    };

    const columns = useMemo(() => ([
        {
            name: t('ProductApprovals.columns.image'),
            cell: row => (
                <ProductImage
                    src={row.image_url || '/placeholder-product.png'}
                    alt={row.product_name}
                    onError={(e) => { e.target.src = '/placeholder-product.png'; }}
                />
            ),
            width: '70px',
        },
        {
            name: t('ProductApprovals.columns.productName'),
            selector: row => row.product_name,
            sortable: true,
            grow: 2,
            cell: row => (
                <ProductNameCell>
                    <span className="name">{row.product_name || '-'}</span>
                    {row.model_number && <span className="model">{row.model_number}</span>}
                </ProductNameCell>
            ),
        },
        {
            name: t('ProductApprovals.columns.category'),
            selector: row => row.category_core?.path || row.category_core?.name || '-',
            sortable: false,
            grow: 1,
        },
        {
            name: t('ProductApprovals.columns.identifiers'),
            cell: row => (
                <IdentifiersCell>
                    {row.ean && <span>EAN: {row.ean}</span>}
                    {row.upc && <span>UPC: {row.upc}</span>}
                    {!row.ean && !row.upc && <span>-</span>}
                </IdentifiersCell>
            ),
            width: '160px',
        },
        {
            name: t('ProductApprovals.columns.source'),
            selector: row => row.virtual_source,
            sortable: false,
            width: '120px',
            cell: row => (
                <SourceBadge>
                    {row.virtual_source === 'scanned_bill'
                        ? t('ProductApprovals.source.scannedBill')
                        : row.virtual_source || '-'
                    }
                </SourceBadge>
            ),
        },
        {
            name: t('ProductApprovals.columns.submittedAt'),
            selector: row => row.created_at,
            sortable: true,
            width: '140px',
            cell: row => new Date(row.created_at).toLocaleDateString(),
        },
        {
            name: t('ProductApprovals.columns.status'),
            selector: row => row.approval_status,
            sortable: true,
            width: '130px',
            cell: row => getStatusBadge(row.approval_status),
        },
        {
            name: t('ProductApprovals.columns.actions'),
            cell: row => (
                <ActionsCell>
                    <ActionButton
                        onClick={() => navigate(`/product-approvals/${row.id}`)}
                        title={t('ProductApprovals.actions.review')}
                    >
                        <MdVisibility size={18} />
                    </ActionButton>
                </ActionsCell>
            ),
            width: '80px',
        },
    ]), [navigate, t]);

    return (
        <PageContainer>
            <PageHeader>
                <TitleRow>
                    <div>
                        <PageTitle>{t('ProductApprovals.title')}</PageTitle>
                        <Subtitle>{t('ProductApprovals.subtitle')}</Subtitle>
                    </div>
                </TitleRow>
            </PageHeader>
            <PageContent>
                {stats && (
                    <StatsGrid>
                        <StatCard
                            title={t('ProductApprovals.stats.pending')}
                            value={stats.pending ?? 0}
                            icon={<MdPending />}
                            color="warning"
                        />
                        <StatCard
                            title={t('ProductApprovals.stats.approved')}
                            value={stats.approved ?? 0}
                            icon={<MdCheckCircle />}
                            color="success"
                        />
                        <StatCard
                            title={t('ProductApprovals.stats.rejected')}
                            value={stats.rejected ?? 0}
                            icon={<MdCancel />}
                            color="danger"
                        />
                        <StatCard
                            title={t('ProductApprovals.stats.merged')}
                            value={stats.merged ?? 0}
                            icon={<MdMerge />}
                            color="info"
                        />
                    </StatsGrid>
                )}

                <FilterBar>
                    <SearchInput
                        type="text"
                        placeholder={t('ProductApprovals.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <FilterGroup>
                        <FilterLabel htmlFor="status-filter">
                            {t('ProductApprovals.filters.status')}
                        </FilterLabel>
                        <FilterSelect
                            id="status-filter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">{t('ProductApprovals.filters.all')}</option>
                            <option value="pending">{t('ProductApprovals.status.pending')}</option>
                            <option value="approved">{t('ProductApprovals.status.approved')}</option>
                            <option value="rejected">{t('ProductApprovals.status.rejected')}</option>
                            <option value="merged">{t('ProductApprovals.status.merged')}</option>
                        </FilterSelect>
                    </FilterGroup>
                </FilterBar>

                {loading ? (
                    <LoadingMessage>{t('ProductApprovals.loading')}</LoadingMessage>
                ) : error ? (
                    <ErrorMessage>{t('ProductApprovals.error')}</ErrorMessage>
                ) : rows.length === 0 ? (
                    <EmptyState>
                        <MdInventory size={48} />
                        <h3>{t('ProductApprovals.empty.title')}</h3>
                        <p>{t('ProductApprovals.empty.description')}</p>
                    </EmptyState>
                ) : (
                    <TableWrapper>
                        <DataTable
                            columns={columns}
                            data={rows}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangeRowsPerPage={setPerPage}
                            onChangePage={setCurrentPage}
                            highlightOnHover
                            pointerOnHover
                            onRowClicked={(row) => navigate(`/product-approvals/${row.id}`)}
                            customStyles={tableStyles}
                        />
                    </TableWrapper>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default ManufacturerProductApprovals;

// Custom table styles
const tableStyles = {
    headRow: {
        style: {
            backgroundColor: '#F9FAFB',
            borderBottom: '1px solid #E5E7EB',
        },
    },
    headCells: {
        style: {
            fontWeight: '600',
            color: '#374151',
            fontSize: '13px',
        },
    },
    rows: {
        style: {
            '&:hover': {
                backgroundColor: '#F3F4F6',
            },
        },
    },
};

// Styled Components
const TitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const Subtitle = styled.p`
    margin: 0.25rem 0 0 0;
    color: #6B7280;
    font-size: 0.875rem;
`;

const FilterBar = styled.div`
    display: flex;
    gap: 1rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
    align-items: center;
`;

const SearchInput = styled.input`
    padding: 0.625rem 0.875rem;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 0.875rem;
    min-width: 280px;

    &:focus {
        outline: none;
        border-color: rgba(130, 22, 146, 0.5);
        box-shadow: 0 0 0 3px rgba(130, 22, 146, 0.1);
    }

    &::placeholder {
        color: #9CA3AF;
    }
`;

const FilterGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const FilterLabel = styled.label`
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
`;

const FilterSelect = styled(SimpleSelect)`
    padding: 0.625rem 0.875rem;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 0.875rem;
    color: #374151;
    background: white;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: rgba(130, 22, 146, 0.5);
        box-shadow: 0 0 0 3px rgba(130, 22, 146, 0.1);
    }
`;

const TableWrapper = styled.div`
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
`;

const ProductImage = styled.img`
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 8px;
    background: #F3F4F6;
`;

const ProductNameCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;

    .name {
        font-weight: 500;
        color: #111827;
    }

    .model {
        font-size: 0.75rem;
        color: #6B7280;
    }
`;

const IdentifiersCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    font-size: 0.75rem;
    color: #6B7280;
`;

const SourceBadge = styled.span`
    display: inline-block;
    padding: 0.25rem 0.5rem;
    background: #F3F4F6;
    color: #6B7280;
    border-radius: 4px;
    font-size: 0.75rem;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ $color }) => $color};
    background: ${({ $bg }) => $bg};
`;

const ActionsCell = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 6px;
    background: #F3F4F6;
    color: #6B7280;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: rgba(130, 22, 146, 0.1);
        color: rgba(130, 22, 146, 1);
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3.75rem 1.25rem;
    text-align: center;
    color: #6B7280;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    svg {
        color: rgba(130, 22, 146, 0.5);
        margin-bottom: 1rem;
    }

    h3 {
        margin: 0 0 0.5rem 0;
        color: #374151;
        font-size: 1.125rem;
    }

    p {
        margin: 0;
        font-size: 0.875rem;
    }
`;
